import { useEffect, useState } from 'react'
import { GripVertical, Plus, Trash2, Loader2, Save, RotateCcw, Pencil, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useStrategySettingsStore } from '@/stores/strategy-settings-store'
import type { GoNoGoTemplateCriterion, GngCategoryDef } from '@/stores/strategy-settings-store'
import { useAuthStore } from '@/stores/auth-store'

const DEFAULT_CATEGORIES: GngCategoryDef[] = [
  { slug: 'strategic', label: 'Strategic Fit', sortOrder: 1 },
  { slug: 'capability', label: 'Capability', sortOrder: 2 },
  { slug: 'commercial', label: 'Commercial', sortOrder: 3 },
  { slug: 'risk', label: 'Risk', sortOrder: 4 },
  { slug: 'resource', label: 'Resource', sortOrder: 5 },
  { slug: 'relationship', label: 'Relationship', sortOrder: 6 },
]

function slugify(label: string): string {
  return label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
}

export function GoNoGoTemplateSettings() {
  const { template, isLoadingTemplate, isSavingTemplate, loadTemplate, saveTemplate } = useStrategySettingsStore()
  const role = useAuthStore(s => s.role)
  const isAdmin = role === 'admin'

  const [thresholds, setThresholds] = useState({ go: 70, conditionalGo: 50, noGo: 0 })
  const [criteria, setCriteria] = useState<GoNoGoTemplateCriterion[]>([])
  const [categories, setCategories] = useState<GngCategoryDef[]>(DEFAULT_CATEGORIES)
  const [dirty, setDirty] = useState(false)

  // Add category state
  const [newCategoryLabel, setNewCategoryLabel] = useState('')

  // Rename category state
  const [editingCategorySlug, setEditingCategorySlug] = useState<string | null>(null)
  const [editingCategoryLabel, setEditingCategoryLabel] = useState('')

  useEffect(() => {
    void loadTemplate()
  }, [loadTemplate])

  useEffect(() => {
    if (template) {
      setThresholds(template.thresholds)
      setCriteria([...template.criteria])
      setCategories(template.categories?.length ? template.categories : DEFAULT_CATEGORIES)
      setDirty(false)
    }
  }, [template])

  const handleSave = async () => {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    const saved = await saveTemplate({
      thresholds,
      criteria: criteria.map((c, i) => ({
        ...(UUID_RE.test(c.id) ? { id: c.id } : {}),
        category: c.category,
        title: c.title,
        description: c.description,
        weight: c.weight,
        isMandatory: c.isMandatory,
        isAiScorable: c.isAiScorable,
        sortOrder: i + 1,
      })),
      categories,
    })
    if (saved) setDirty(false)
  }

  const updateCriterion = (id: string, updates: Partial<GoNoGoTemplateCriterion>) => {
    setCriteria(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c))
    setDirty(true)
  }

  const removeCriterion = (id: string) => {
    setCriteria(prev => prev.filter(c => c.id !== id))
    setDirty(true)
  }

  const addCriterion = (category: string) => {
    const newCriterion: GoNoGoTemplateCriterion = {
      id: `new-${Date.now()}`,
      templateId: template?.id ?? '',
      category,
      title: '',
      description: null,
      weight: 5,
      isMandatory: false,
      isAiScorable: false,
      sortOrder: criteria.length + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setCriteria(prev => [...prev, newCriterion])
    setDirty(true)
  }

  const handleAddCategory = () => {
    const label = newCategoryLabel.trim()
    if (!label) return
    const slug = slugify(label)
    if (!slug || categories.some(c => c.slug === slug)) return
    setCategories(prev => [...prev, { slug, label, sortOrder: prev.length + 1 }])
    setNewCategoryLabel('')
    setDirty(true)
  }

  const handleStartRename = (catDef: GngCategoryDef) => {
    setEditingCategorySlug(catDef.slug)
    setEditingCategoryLabel(catDef.label)
  }

  const handleConfirmRename = () => {
    const label = editingCategoryLabel.trim()
    if (!label || !editingCategorySlug) {
      setEditingCategorySlug(null)
      return
    }
    setCategories(prev => prev.map(c =>
      c.slug === editingCategorySlug ? { ...c, label } : c
    ))
    setEditingCategorySlug(null)
    setDirty(true)
  }

  const handleDeleteCategory = (slug: string) => {
    setCategories(prev => prev.filter(c => c.slug !== slug))
    setDirty(true)
  }

  const getCriteriaCount = (slug: string) => criteria.filter(c => c.category === slug).length

  if (isLoadingTemplate) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const criteriaByCategory = new Map<string, GoNoGoTemplateCriterion[]>()
  for (const c of criteria) {
    const existing = criteriaByCategory.get(c.category) ?? []
    existing.push(c)
    criteriaByCategory.set(c.category, existing)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">Go / No-Go Template</h3>
          <p className="text-meta mt-0.5">Default criteria and thresholds for all new assessments.</p>
        </div>
        {isAdmin && dirty && (
          <Button size="sm" onClick={() => void handleSave()} disabled={isSavingTemplate}>
            {isSavingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save Template
          </Button>
        )}
      </div>

      {!isAdmin && (
        <div className="bg-muted/50 rounded-lg p-3 text-meta">
          Only organisation admins can edit the default template.
        </div>
      )}

      {/* Thresholds */}
      <div className="border border-border rounded-xl bg-card shadow-sm p-5">
        <h4 className="text-sm font-medium mb-3">Score Thresholds</h4>
        <p className="text-meta mb-4">Define what score ranges map to each recommendation.</p>

        <div className="grid grid-cols-3 gap-4">
          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-6 w-6 rounded bg-[hsl(var(--status-success)/0.1)] flex items-center justify-center">
                <span className="text-xs font-bold text-[hsl(var(--status-success))]">G</span>
              </div>
              <span className="text-sm font-medium">Go</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-meta">Score &ge;</span>
              <Input
                type="number"
                value={thresholds.go}
                onChange={e => { setThresholds(t => ({ ...t, go: parseInt(e.target.value) || 0 })); setDirty(true) }}
                className="w-16 h-8 text-center text-sm"
                disabled={!isAdmin}
              />
            </div>
          </div>

          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-6 w-6 rounded bg-[hsl(var(--status-warning)/0.1)] flex items-center justify-center">
                <span className="text-xs font-bold text-[hsl(var(--status-warning))]">C</span>
              </div>
              <span className="text-sm font-medium">Conditional go</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={thresholds.conditionalGo}
                onChange={e => { setThresholds(t => ({ ...t, conditionalGo: parseInt(e.target.value) || 0 })); setDirty(true) }}
                className="w-16 h-8 text-center text-sm"
                disabled={!isAdmin}
              />
              <span className="text-meta">to {thresholds.go - 1}</span>
            </div>
          </div>

          <div className="border border-border rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-6 w-6 rounded bg-[hsl(var(--destructive)/0.1)] flex items-center justify-center">
                <span className="text-xs font-bold text-[hsl(var(--destructive))]">N</span>
              </div>
              <span className="text-sm font-medium">No go</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-meta">Score &lt;</span>
              <span className="text-sm font-medium">{thresholds.conditionalGo}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Criteria list */}
      <div className="border border-border rounded-xl bg-card shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h4 className="text-sm font-medium">Default Criteria</h4>
          <span className="text-meta">{criteria.length} criteria</span>
        </div>

        {categories.map(catDef => {
          const catCriteria = criteriaByCategory.get(catDef.slug) ?? []
          const isEditing = editingCategorySlug === catDef.slug
          const canDelete = getCriteriaCount(catDef.slug) === 0

          return (
            <div key={catDef.slug} className="border-b border-border last:border-b-0">
              <div className="px-5 py-2.5 bg-muted/30 flex items-center justify-between group/cat">
                {isEditing ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={editingCategoryLabel}
                      onChange={e => setEditingCategoryLabel(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleConfirmRename()
                        if (e.key === 'Escape') setEditingCategorySlug(null)
                      }}
                      className="h-6 w-48 text-xs font-medium uppercase tracking-wider px-1.5"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleConfirmRename}
                      className="p-0.5 rounded hover:bg-muted"
                    >
                      <Check className="h-3 w-3 text-[hsl(var(--status-success))]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingCategorySlug(null)}
                      className="p-0.5 rounded hover:bg-muted"
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{catDef.label}</span>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => handleStartRename(catDef)}
                        className="p-0.5 rounded hover:bg-muted opacity-0 group-hover/cat:opacity-100 transition-opacity"
                      >
                        <Pencil className="h-3 w-3 text-muted-foreground" />
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{catCriteria.length} criteria</span>
                  {isAdmin && (
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(catDef.slug)}
                              disabled={!canDelete}
                              className="p-0.5 rounded hover:bg-muted opacity-0 group-hover/cat:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                            </button>
                          </span>
                        </TooltipTrigger>
                        {!canDelete && (
                          <TooltipContent side="left">
                            <p className="text-xs">Remove all criteria first</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
              <div className="divide-y divide-border">
                {catCriteria.map(c => (
                  <div key={c.id} className="px-5 py-3 flex items-center justify-between group">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <GripVertical className="h-4 w-4 text-muted-foreground/40 flex-shrink-0" />
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isAdmin ? (
                          <Input
                            value={c.title}
                            onChange={e => updateCriterion(c.id, { title: e.target.value })}
                            className="h-7 text-sm border-transparent bg-transparent hover:border-border focus:border-border px-1"
                          />
                        ) : (
                          <span className="text-sm">{c.title}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {c.isMandatory && (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0 font-semibold flex-shrink-0">Mandatory</Badge>
                      )}
                      {c.isAiScorable && (
                        <Badge className="text-[10px] px-1.5 py-0 bg-[hsl(var(--ai-accent-soft))] text-[hsl(var(--ai-accent-text))] border-transparent flex-shrink-0">AI</Badge>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="text-meta">Wt:</span>
                        <Input
                          type="number"
                          min={1}
                          max={10}
                          value={c.weight}
                          onChange={e => updateCriterion(c.id, { weight: parseInt(e.target.value) || 1 })}
                          className="w-12 h-7 text-xs text-center"
                          disabled={!isAdmin}
                        />
                      </div>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => removeCriterion(c.id)}
                          className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {isAdmin && (
                  <div className="px-5 py-2">
                    <button
                      type="button"
                      onClick={() => addCriterion(catDef.slug)}
                      className="text-meta hover:text-foreground flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Add criterion
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {/* Add category */}
        {isAdmin && (
          <div className="px-5 py-3 border-t border-border flex items-center gap-2">
            <Input
              placeholder="New category name..."
              value={newCategoryLabel}
              onChange={e => setNewCategoryLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddCategory() }}
              className="h-8 text-sm max-w-xs"
            />
            <Button
              size="sm"
              variant="ghost"
              onClick={handleAddCategory}
              disabled={!newCategoryLabel.trim() || isSavingTemplate}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Category
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      {isAdmin && (
        <div className="flex items-center justify-between text-meta">
          <button
            type="button"
            onClick={() => {
              if (template) {
                setCriteria([...template.criteria])
                setThresholds(template.thresholds)
                setCategories(template.categories?.length ? template.categories : DEFAULT_CATEGORIES)
                setDirty(false)
              }
            }}
            className="flex items-center gap-1 hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Discard changes
          </button>
          <span>Total weight: {criteria.reduce((s, c) => s + c.weight, 0)}</span>
        </div>
      )}
    </div>
  )
}
